import { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { FreeMode, Mousewheel } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import { StoryCircle } from 'components-ref/molecules'
import { useModalContext } from 'context/modalContext'
import {
  getCachedBlob,
  prefetchImageToBlob
} from 'helpers/mediaPrefetch'
import { collectStoryPrefetchUrls } from 'helpers/storyPrefetch'
import { storyTimeAgo } from 'lib/dayjs'
import { StoryPreviewModal } from 'pages/storyPreview'
import { useUserProfile } from 'store'
import { type Story, type StoryFeedGroup } from 'types/api/story'

import 'swiper/css'
import 'swiper/css/free-mode'

interface StoriesListProps {
  stories?: Story[] | StoryFeedGroup[]
  page: 'profile' | 'feed' | string
  id?: string
  username?: string
  displayName?: string
  avatar?: string | null
}

export const StoriesList = ({
  stories,
  page,
  id,
  username,
  displayName,
  avatar,
}: StoriesListProps) => {
  const userData = useUserProfile()
  const { t } = useTranslation()
  const modalData = useModalContext()

  const idFromUrl = id

  const isOwnProfile =
    !!idFromUrl &&
    userData?.id === idFromUrl

  const canSeeProcessingStatus =
    page === 'profile' &&
    isOwnProfile

  const swiperProps = {
    slidesPerView: 'auto' as const,
    spaceBetween: 1,
    freeMode: {
      enabled: true,
      momentum: true,
      momentumBounce: false,
      momentumRatio: 0.95,
      momentumVelocityRatio: 0.7,
    },
    touchRatio: 1.15,
    resistance: true,
    resistanceRatio: 0.12,
    touchReleaseOnEdges: true,
    watchOverflow: true,
    grabCursor: true,
    mousewheel: {
      forceToAxis: true,
    },
    modules: [
      FreeMode,
      Mousewheel,
    ],
    breakpoints: {
      0: {
        touchRatio: 1.1,
        freeMode: {
          momentumVelocityRatio: 0.8,
          momentumRatio: 0.95,
        },
      },
      750: {
        touchRatio: 1,
        freeMode: {
          momentumVelocityRatio: 0.75,
          momentumRatio: 0.93,
        },
      },
      1024: {
        touchRatio: 0.7,
        freeMode: {
          momentumVelocityRatio: 0.6,
          momentumRatio: 0.9,
        },
      },
    },
  }

  // Profile endpoint returns a flat Story[]
const profileStories = useMemo<Story[]>(() => {
  if (page !== 'profile' || !Array.isArray(stories)) {
    return []
  }

  return stories as Story[]
}, [stories, page])


  // Feed endpoint already returns creator-grouped StoryFeedGroup[]
  const feedBuckets = useMemo<StoryFeedGroup[]>(() => {
    if (page !== 'feed') {
      return []
    }

    if (!Array.isArray(stories)) {
      return []
    }

    return stories as StoryFeedGroup[]
  }, [
    stories,
    page,
  ])

type StoryCreator = {
  userId?: string
  username?: string
  displayName?: string
  avatar?: string | null
}

const prefetchStories = useMemo<Story[]>(() => {
  if (page === 'profile') {
    return profileStories
  }

  if (page === 'feed') {
    const selected: Story[] = []

    for (const bucket of feedBuckets) {
      const creatorStories = bucket.stories ?? []

      if (!creatorStories.length) continue

      // Must match the startIndex used when opening the circle.
      const firstUnseenIndex = creatorStories.findIndex(
        story => !story.seen
      )

      const startIndex = firstUnseenIndex >= 0
        ? firstUnseenIndex
        : 0

      selected.push(creatorStories[startIndex])
    }

    return selected
  }

  return []
}, [page, profileStories, feedBuckets])

const prefetchUrls = useMemo(
  () => collectStoryPrefetchUrls(prefetchStories),
  [prefetchStories]
)

// Trigger a render when the prefetched images are ready,
// so profile circles can consume the new blob URLs.
const [, setCacheRevision] = useState(0)

useEffect(() => {
  if (!prefetchUrls.length) return

  let active = true
  let nextIndex = 0

  // Limit concurrent image downloads.
  const concurrency = Math.min(3, prefetchUrls.length)

  const workers = Array.from(
    { length: concurrency },
    async () => {
    while (nextIndex < prefetchUrls.length) {
      if (!active) break

      const url = prefetchUrls[nextIndex++]
        if (!getCachedBlob(url)) {
          await prefetchImageToBlob(url)
        }

        if (active) {
          setCacheRevision(value => value + 1)
        }
      }
    }
  )

  void Promise.all(workers)

  return () => {
    active = false
  }
}, [prefetchUrls])


const openStoryModal = (
  modalStories: Story[],
  startIndex = 0,
  creator: StoryCreator = {}
) => {
  if (!modalStories.length) return

  modalData.addModal(
    '',
    <StoryPreviewModal
      stories={modalStories}
      startIndex={startIndex}
      homeFeed={false}
      userId={creator.userId}
      username={creator.username}
      displayName={creator.displayName}
      avatar={creator.avatar}
      withCloseButton={true}
      onClose={() => modalData.clearModal()}
    />,
    undefined,
    false,
    'story__modal'
  )
}

const handleStoryKeyDown = (
  event: React.KeyboardEvent<HTMLDivElement>,
  modalStories: Story[],
  startIndex: number,
  creator: StoryCreator
) => {
  if (event.key !== 'Enter' && event.key !== ' ') return

  event.preventDefault()

  openStoryModal(modalStories, startIndex, creator)
}
  return (
    <StoriesListWrapper className='stories_list_wrapper'>
      <StoriesWrapper className='stories_wrapper'>
        <Swiper
          {...swiperProps}
          className='stories-swiper'
        >
          {page === 'profile' &&
            userData?.role === 'model' &&
            isOwnProfile && (
              <SwiperSlide
                style={{
                  width: 'auto',
                }}
              >
                <Link to='/new/story'>
                  <AddStory>
                    <StoryCircle
                      text={t('addStory')}
                      type='add'
                    />
                  </AddStory>
                </Link>
              </SwiperSlide>
            )}

          {page === 'profile' &&
            profileStories.map(
              (
                story,
                index
              ) => {
                const thumbUrl =
                  story.image_src_thumb ??
                  story.video_thumb_src

                const circleImg = thumbUrl
                  ? getCachedBlob(thumbUrl) ?? thumbUrl
                  : avatar ?? placeholderAvatar

                return (
                  <SwiperSlide
                    key={`profile-${story.id ?? index}`}
                    style={{
                      width: 'auto',
                    }}
                  >
                    <StoryButton
                      role='button'
                      tabIndex={0}
                      onClick={() => {
                        openStoryModal(profileStories, index, {
                          userId: id,
                          username,
                          displayName,
                          avatar
                        })
                      }}
                      onKeyDown={event => {
                        handleStoryKeyDown(event, profileStories, index, {
                          userId: id,
                          username,
                          displayName,
                          avatar
                        })
                      }}
                    >
                      <StoryCircle
                        img={circleImg ?? undefined}
                        text={storyTimeAgo(
                          story.created_at
                        )}
                        viewed={!!story.seen}
                        type='story'
                        status={canSeeProcessingStatus ? story.status : 'finished'}
                      />
                    </StoryButton>
                  </SwiperSlide>
                )
              }
            )}

          {page === 'feed' &&
            feedBuckets.map(
              (
                bucket,
                index
              ) => {
                const creatorStories =
                  bucket.stories ?? []
                const firstUnseenIndex = creatorStories.findIndex(
                  story => !story.seen
                )

                const startIndex = firstUnseenIndex >= 0
                  ? firstUnseenIndex
                  : 0

                const allStoriesSeen =
                  creatorStories.length > 0 &&
                  creatorStories.every(story => story.seen)

                const creator = {
                  userId: bucket.user_id,
                  username: bucket.user?.username,
                  displayName: bucket.user?.display_name,
                  avatar: bucket.user?.avatar?.url ?? null
                }
                const latest =
                  creatorStories[0]

                if (!latest) {
                  return null
                }

                const circleImg =
                  bucket.user?.avatar?.url ??
                  latest.image_src_thumb ??
                  latest.image_src ??
                  latest.video_thumb_src ??
                  placeholderAvatar

                const circleText =
                  bucket.user?.display_name ??
                  bucket.user?.username ??
                  `User ${bucket.user_id}`

                return (
                  <SwiperSlide
                    key={`feed-${bucket.user_id}-${index}`}
                    style={{
                      width: 'auto',
                    }}
                  >
                    <StoryButton
                      role='button'
                      tabIndex={0}
                      onClick={() => {
                        openStoryModal(
                          creatorStories,
                          startIndex,
                          creator
                        )
                      }}
                      onKeyDown={event => {
                        handleStoryKeyDown(
                          event,
                          creatorStories,
                          startIndex,
                          creator
                        )
                      }}
                    >
                      <StoryCircle
                        img={circleImg}
                        text={circleText}
                        viewed={allStoriesSeen}
                        type='story'
                        status='finished'
                      />
                    </StoryButton>
                  </SwiperSlide>
                )
              }
            )}
        </Swiper>
      </StoriesWrapper>
    </StoriesListWrapper>
  )
}

export default StoriesList

const StoriesListWrapper = styled.div`
  width: 100%;
  padding: 0 1.5rem;
  display: flex;
`

const StoriesWrapper = styled.div`
  width: 100%;
  display: flex;

  .stories-swiper {
    width: 100%;
  }

  .swiper-slide {
    outline: none;
  }
`

const StoryButton = styled.div`
  cursor: pointer;
`

const AddStory = styled.div`
  height: 100%;
  margin-right: 2rem;
  min-width: 5.8rem;
`